import React, { useCallback, useRef, useState, useEffect } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Input, Select} from 'antd';
import type { InputRef } from 'antd';
import { WidgetProps } from '@rjsf/utils';

const index = 0;

interface Option {
  label: string; 
  value: string;
  tags?: string[];
}

interface SchemaOption {
  label: string;
  value: string;
  tags?: string[];
  template_id?: number;
}

const SelectWithInput = (props: WidgetProps) => {
  const {
      value,
      onChange,
      disabled,
      readonly,
      schema,
  } = props;

  const [isDetailMode, setIsDetailMode] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  // Check if we're in detail mode by looking at the closest parent container
  // If in detail mode, we disable the widget
  useEffect(() => {
    const checkMode = () => {
      if (selectRef.current) {
        // Walk up the DOM tree to find if we're inside a detailMode container
        let element = selectRef.current.parentElement;
        while (element) {
          if (element.classList.contains('detailMode')) {
            setIsDetailMode(true);
            return;
          }
          if (element.classList.contains('overviewMode')) {
            setIsDetailMode(false);
            return;
          }
          element = element.parentElement;
        }
        // If no mode class found, default to overview (not disabled)
        setIsDetailMode(false);
      }
    };

    // Check mode immediately
    checkMode();

    // Set up a MutationObserver to watch for DOM changes
    const observer = new MutationObserver(() => {
      checkMode();
    });

    // Observe changes to the document and all child elements
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Widget should be disabled if explicitly disabled, readonly, or in detail mode
  const isDisabled = disabled || readonly || isDetailMode;


  const [name, setName] = useState('');
  const [localOptions, setLocalOptions] = useState<Option[]>([]);
  const inputRef = useRef<InputRef>(null);

  // Generate options from schema with support for both old enum format and new options format
  const getOptions = (): Option[] => {
    try {
      // Check if schema has options with tags (new format)
      if (schema.options && Array.isArray(schema.options)) {
        return schema.options.map((option: SchemaOption) => ({
          label: option.label || option.value || '',
          value: option.value || '',
          tags: option.tags || []
        })).filter((option: Option) => option.value !== ''); // Filter out empty options
      }
      
      // Fallback to enum format (old format) - user-added options won't have tags
      if (schema.enum && Array.isArray(schema.enum) && schema.enum.length > 0) {
        return schema.enum
          .filter((item: any) => typeof item === 'string' && item.trim() !== '') // Filter out non-strings and empty strings
          .map((item: string) => ({
            label: item,
            value: item,
            tags: [] // User-added options don't have tags
          }));
      }
      
      return [];
    } catch (error) {
      console.error('Error generating options for SelectWithInputWidget:', error);
      return [];
    }
  };

  // Combine schema options with locally added options
  const schemaOptions = getOptions();
  const options = [...schemaOptions, ...localOptions.filter((localOpt: Option) => 
    !schemaOptions.some((schemaOpt: Option) => schemaOpt.value === localOpt.value)
  )];

  // Ensure current value is in local options if it's a valid value (not schema title)
  useEffect(() => {
    if (value && value !== schema.title && !options.some((opt: Option) => opt.value === value)) {
     
      const missingOption: Option = {
        label: value,
        value: value,
        tags: []
      };
      setLocalOptions(prev => [...prev, missingOption]);
    }
  }, [value, options.length, schema.title]);

  // Update local options when schema changes
  useEffect(() => {
    const getSchemaOptions = () => {
      try {
        if (schema.options && Array.isArray(schema.options)) {
          return schema.options.map((option: SchemaOption) => ({
            label: option.label || option.value || '',
            value: option.value || '',
            tags: option.tags || []
          })).filter((option: Option) => option.value !== '');
        }
        
        if (schema.enum && Array.isArray(schema.enum)) {
          return schema.enum
            .filter((item: any) => typeof item === 'string' && item.trim() !== '')
            .map((item: string) => ({
              label: item,
              value: item,
              tags: []
            }));
        }
        
        return [];
      } catch (error) {
        console.error('Error getting schema options:', error);
        return [];
      }
    };

    const schemaOptions = getSchemaOptions();
    setLocalOptions(prev => prev.filter((localOpt: Option) => 
      !schemaOptions.some((schemaOpt: Option) => schemaOpt.value === localOpt.value)
    ));
  }, [schema.options, schema.enum]);

  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  /*eslint-disable @typescript-eslint/no-unused-vars */
  const addItem = () => {
    const newItemName = (name || `New item ${index - 1}`).trim();
    
    // Don't add empty options
    if (!newItemName) {
      return;
    }
    
    setName('');
    
    // Check if the option already exists in current options
    const existingInOptions = options.some((opt: Option) => opt.value === newItemName);
    
    if (existingInOptions) {
      // If option already exists, just select it
      handleChange(newItemName);
      return;
    }
    
    // Add to local options
    const newOption: Option = {
      label: newItemName,
      value: newItemName,
      tags: [] // User-added options don't have tags
    };
    
    setLocalOptions(prev => [...prev, newOption]);
    
    // CRITICAL: Update the schema enum to prevent validation errors
    // RJSF validator needs to know about the new option
    try {
      if (schema.enum && Array.isArray(schema.enum)) {
        if (!schema.enum.includes(newItemName)) {
          schema.enum.push(newItemName);
        }
      } else if (schema.options && Array.isArray(schema.options)) {
        if (!schema.options.some((opt: SchemaOption) => opt.value === newItemName)) {
          schema.options.push({
            label: newItemName,
            value: newItemName,
            tags: []
          });
        }
      }

    } catch (error) {
      console.error('Failed to update schema with new option:', error);
    }
    
    // Set the newly added option as the selected value
    handleChange(newItemName);
    
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleChange = useCallback(
      (newValue: string) => {
        // Ensure we're not setting the schema title as a value
        if (newValue && newValue !== schema.title) {
          onChange(newValue);
        } else {
          console.warn('Attempted to set schema title as value, ignoring:', newValue);
        }
      },
      [onChange, schema.title]
    );

  return (
    <div className='' ref={selectRef}>
    <Select
      value={value && value !== schema.title ? value : undefined}
      disabled={isDisabled}
      showSearch
      placeholder="Vul jouw maatregel in"
      filterOption={(input, option) => {
        if (!option) return false;
        const searchTerm = input.toLowerCase();
        const labelMatch = (option.label as string).toLowerCase().includes(searchTerm);
        // Search in tags if available
        const optionData = options.find((opt: Option) => opt.value === option.value);
        const tagMatch = optionData?.tags ? optionData.tags.some((tag: string) => 
          tag.toLowerCase().includes(searchTerm)
        ) : false;
        return labelMatch || tagMatch;
      }}
      onChange={handleChange}
     dropdownRender={(menu) => (
        <>
          {menu}
          {!isDisabled && (
            <div className='flex flex-row justify-between w-full'>
              <Input
                placeholder="Nieuwe maatregel"
                ref={inputRef}
                value={name}
                onChange={onNameChange}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter' && name.trim()) {
                    e.preventDefault();
                    addItem();
                  }
                }}
                disabled={isDisabled}
              />
              <Button type="text" icon={<PlusOutlined />} onClick={addItem} disabled={isDisabled}>
                Toevoegen
              </Button>
            </div>
          )}
        </>
      )}
      options={options.map((option: Option) => ({
        label: option.label,
        value: option.value,
        // Store tags as data attribute for filtering
        'data-tags': option.tags?.join(' ') || ''
      }))}
    />
    </div>
  );
};

export default SelectWithInput;